import { omit } from "lodash";
import { getFromDataPath, mutateByPath } from "../../../utility";
import {
  ExpressionLogicalOperatorEnum,
  WhereClauseCompoundExpression,
  WhereClauseWithCompoundExpression,
} from "../../ars_ldm";
import { isWhereClause, isWhereClauseWithCondition } from "../../typeGuards";
import { ArsContextState, IArsActionNotWhereClause } from "../interface";
import { addPatchToStack } from "./addPatchToStack";

/**
 * Applies a NOT operator to a where clause.
 *
 * @param state - Current ARS context state
 * @param action - Add-NOT action
 * @returns Updated state with NOT-wrapped clause
 */
export const arsc_addNotToWhereClause = <T>(
  state: ArsContextState<T>,
  action: IArsActionNotWhereClause,
): ArsContextState<T> => {
  const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
  if (!whereClause) {
    throw new Error(`Invalid dataPath for NOT_WHERE_CLAUSE: ${action.dataPath}`);
  }

  if (isWhereClauseWithCondition(whereClause)) {
    const newCompoundExpression: WhereClauseCompoundExpression = {
      logicalOperator: ExpressionLogicalOperatorEnum.NOT,
      whereClauses: [
        {
          id: crypto.randomUUID(),
          subClauseId: "",
          condition: whereClause.condition,
          level: whereClause.level + 1,
          order: 0,
        },
      ],
    };
    const newEntity = {
      ...omit(whereClause, "condition"),
      compoundExpression: newCompoundExpression,
      level: whereClause.level,
      order: whereClause.order,
    };

    return addPatchToStack(
      state,
      mutateByPath({ ...state, changed: true }, action.dataPath, newEntity),
    );
  }

  const ce = whereClause.compoundExpression;
  if (
    (ce.whereClauses?.length ??
      // istanbul ignore next
      0) > 1
  ) {
    throw new Error(
      `Cannot apply NOT operator to ${action.dataPath} because it has multiple sub-clauses`,
    );
  }

  const newCompoundExpression: WhereClauseCompoundExpression = {
    logicalOperator: ExpressionLogicalOperatorEnum.NOT,
    whereClauses:
      ce.whereClauses ??
      // istanbul ignore next
      [],
  };
  const newWhereClause: WhereClauseWithCompoundExpression = {
    compoundExpression: newCompoundExpression,
    level: whereClause.level,
    order: whereClause.order,
  };

  return addPatchToStack(
    state,
    mutateByPath({ ...state, changed: true }, action.dataPath, newWhereClause),
  );
};
